import { localStorageGet } from '@Infra/LocalStorage';
import { LOCAL_STORAGE_KEYS } from '@Infra/LocalStorage/keys';
import type { DismissalRecord } from '@Application/ApplicationSettings/InstallPrompt/shouldOfferInstallPrompt';

export type PersistedDismissalShape = {
	readonly dismissalCount: number;
	readonly lastDismissedAt: string;
};

const isPersistedDismissalShape = (value: unknown): value is PersistedDismissalShape => {
	if (typeof value !== 'object' || value === null) {
		return false;
	}

	const candidate = value as Record<string, unknown>;

	return typeof candidate.dismissalCount === 'number' && typeof candidate.lastDismissedAt === 'string';
};

/**
 * Synchronous by design, not a thunk: `InstallAppBanner` needs this at mount, before its first
 * paint, to decide whether it should render at all. A thunk dispatched through `useDispatch`
 * resolves on a later tick at best, which would flash the banner visible for a render and then
 * hide it — worse than the direct `@Infra` import this file replaces. Reading `localStorage` is
 * itself synchronous, so wrapping it in a promise would buy nothing and only reintroduce the
 * flicker if someone "harmonised" this with the dispatched write below.
 *
 * Anything that is not a well-formed `{ dismissalCount, lastDismissedAt }` record — no value yet,
 * malformed JSON, the legacy boolean flag this key used to carry — resolves to `null`: corrupted or
 * inherited storage never throws and reads as "no dismissal on record", which
 * `shouldOfferInstallPrompt` treats as eligible. A `dismissalCount` below one (zero, negative,
 * fractional) is floored up to one: it still denotes at least one past refusal, just an imprecise one.
 */
const readInstallPromptDismissal = (): DismissalRecord | null => {
	const rawDismissalState = localStorageGet(LOCAL_STORAGE_KEYS.INSTALL_BANNER_DISMISSAL_STATE);

	if (rawDismissalState === null) {
		return null;
	}

	let parsedValue: unknown;

	try {
		parsedValue = JSON.parse(rawDismissalState);
	} catch {
		return null;
	}

	if (!isPersistedDismissalShape(parsedValue)) {
		return null;
	}

	const lastDismissedAtMs = Date.parse(parsedValue.lastDismissedAt);

	if (Number.isNaN(lastDismissedAtMs)) {
		return null;
	}

	return {
		dismissalCount: Math.max(1, Math.floor(parsedValue.dismissalCount)),
		lastDismissedAtMs,
	};
};

export default readInstallPromptDismissal;
